import React from 'react';
import {
  AlertTriangleIcon,
  FilePlusIcon,
  FileReportIcon,
  PlaneIcon,
  RadarIcon,
  RefreshIcon,
  ShipIcon,
  TrailerIcon,
} from '../components/ui/Icons';
import { NotificationItem, ShipmentKind } from './api';
import {
  STAGE_COLORS,
  STATUS_CATALOGS,
  StatusDef,
  UNKNOWN_COLOR,
  canonStatus,
} from '../shipments/statusCatalogs';
import { ShipmentMode, ShipmentStage } from '../shipments/types';

/**
 * LENGUAJE VISUAL DE LOS AVISOS.
 *
 * ## 🔑 Por qué es un archivo y no está dentro de cada pantalla
 *
 * Lo usan **dos** superficies —la campanita y la página de la bandeja— y va a usarlo una tercera el día que
 * un aviso aparezca dentro del detalle de un embarque. Con una copia en cada una, "retraso" acabaría siendo
 * rojo en un lado y naranja en el otro: nadie lo reporta como error, y sin embargo destruye lo único que un
 * color aporta, que es reconocer el tipo **sin leer**.
 *
 * 📌 Es la misma razón por la que `notificationLink` se mudó aquí.
 *
 * ## ⚠️ El color NUNCA va solo
 *
 * Cada tipo lleva **icono + color + palabra**. Un sistema que solo cambia de color excluye a quien no
 * distingue esos tonos, y obliga a todos los demás a memorizar una convención que nadie les explicó.
 */

/**
 * Tono del aviso. Se devuelve como cadena y **no** como clase de CSS a propósito: cada pantalla tiene su
 * propio módulo de estilos, así que devolver una clase de aquí no se podría resolver del otro lado.
 */
export type EventTone = 'moved' | 'late' | 'docs' | 'note' | 'system';

export interface EventVisual {
  icon: React.ReactNode;
  /** Etiqueta corta, la que va en la insignia. */
  label: string;
  tone: EventTone;
}

/**
 * Identidad de cada tipo de evento.
 *
 * ⚠️ El respaldo importa: los tipos los declara el **backend** (`constants/notifications.ts`), así que uno
 * nuevo llega a esta pantalla sin que nadie toque el frontend. Sin un caso por omisión, ese aviso se
 * dibujaría sin icono y parecería roto — y el nuevo tipo es justo el que nadie va a estar mirando.
 */
export const eventVisual = (eventType: string): EventVisual => {
  switch (eventType) {
    case 'status_change':
      return { icon: <RefreshIcon size={16} />, label: 'Status', tone: 'moved' };
    case 'delayed':
      return { icon: <AlertTriangleIcon size={16} />, label: 'Late', tone: 'late' };
    case 'missing_docs':
      return { icon: <FileReportIcon size={16} />, label: 'Documents', tone: 'docs' };
    case 'note_added':
      return { icon: <FilePlusIcon size={16} />, label: 'Note', tone: 'note' };
    case 'job_summary':
      return { icon: <RadarIcon size={16} />, label: 'Sync', tone: 'system' };
    case 'test':
      return { icon: <RadarIcon size={16} />, label: 'Test', tone: 'system' };
    default:
      return {
        icon: <RadarIcon size={16} />,
        /** 📌 Se formatea el identificador en lugar de poner "Other": `dry_run` se lee como `Dry run`. */
        label: eventType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
        tone: 'system',
      };
  }
};

export interface ModeVisual {
  icon: React.ReactNode;
  /** Tres letras. Ver la nota de abajo sobre por qué no basta el icono. */
  tag: string;
  label: string;
}

/**
 * Identidad de la modalidad.
 *
 * 🔑 **Icono y LETRAS**, igual que en la columna de envíos del detalle de cliente. Un barco y un avión se
 * distinguen bien a 16px, pero un remolque y un barco de costado no tanto — y las tres letras hacen que la
 * distinción no dependa del tamaño ni de la vista de cada quien.
 */
export const modeVisual = (kind: ShipmentKind | null): ModeVisual | null => {
  if (kind === 'ocean') return { icon: <ShipIcon size={14} />, tag: 'OCN', label: 'Ocean' };
  if (kind === 'air') return { icon: <PlaneIcon size={14} />, tag: 'AIR', label: 'Air' };
  if (kind === 'load') return { icon: <TrailerIcon size={14} />, tag: 'LND', label: 'Land' };
  /** 📌 `null` cuando el aviso no es de un embarque: el resumen del JOB, el de prueba. */
  return null;
};

/**
 * Tono de un estatus: qué tan avanzado va el envío. Lo usan el acento de la tarjeta y el punto que late.
 */
export type StatusTone = 'done' | 'moving' | 'waiting' | 'stalled' | 'neutral';

export interface StatusMeaning {
  /** El color de la plataforma para ese estatus: el mismo de las tablas y las tarjetas de estatus. */
  color: string;
  tone: StatusTone;
}

const TONE_OF_STAGE: Record<ShipmentStage, StatusTone> = {
  origin: 'waiting',
  in_transit: 'moving',
  destination: 'done',
  delivered: 'done',
  cancelled: 'stalled',
  unknown: 'neutral',
};

const MODE_OF_KIND: Record<ShipmentKind, ShipmentMode> = { load: 'land', ocean: 'ocean', air: 'air' };

/** El estatus en un catálogo, por su nombre guardado o por su etiqueta de pantalla (`Waiting Load`). */
const findIn = (defs: StatusDef[], token: string): StatusDef | undefined =>
  defs.find((d) => canonStatus(d.key) === token || canonStatus(d.label) === token);

/**
 * Etapa por el **sentido** del texto, para lo que no está en ningún catálogo: el texto libre de un transportista
 * (`DLV - Delivery`, `On Board Vessel`) o un estatus de terrestre que no sale en las tarjetas (`Quotes Received`).
 *
 * 📌 El orden importa: lo detenido va primero, para que *"Delivery delayed"* no se lea como entregado.
 */
const stageByMeaning = (s: string): ShipmentStage => {
  if (/cancel|dry run|void|reject|delay|late|exception|fail|hold/.test(s)) return 'cancelled';
  if (/deliver|complet|proof of delivery|\bpod\b/.test(s)) return 'delivered';
  if (/arriv|landed|discharg|destination|unload/.test(s)) return 'destination';
  if (/transit|depart|sail|flight|en route|on board|moving|vessel/.test(s)) return 'in_transit';
  if (/wait|pending|assign|book|quote|schedul|receiv|gate|load|manifest|pick|ready/.test(s)) return 'origin';
  return 'unknown';
};

/**
 * Color y tono de un estatus.
 *
 * ## 🔴 Por qué cambió (15-oct-2026)
 *
 * *"Hay que estar seguros que tengamos todos los estatus con color, ya que al menos Assigned aparece en negro sin
 * diseño"*. Dos causas: el ámbar de la insignia apuntaba a un token que no existía (`--dylo-warning`, ya definido en
 * `tokens.css`), y la regla adivinaba por palabras sueltas, así que `Arrived`, `Landed`, `Gate In`, `Loaded` y
 * `Received` caían a gris.
 *
 * ## 🔑 Ahora sale de los catálogos de la plataforma (`shipments/statusCatalogs`)
 *
 * Es el mismo color que el estatus tiene en la tabla de cargas y en sus tarjetas: `Assigned` ámbar, `On Transit` azul,
 * `At Destination` verde. Se busca primero en el catálogo de su modalidad, luego en los otros dos y, para el texto libre
 * de un transportista, por el sentido de las palabras. Solo un texto que no se reconoce queda gris: pintar de verde
 * algo que no se reconoció sería afirmar que llegó bien sin saberlo.
 */
export const statusMeaning = (
  status: string | null | undefined,
  kind?: ShipmentKind | null
): StatusMeaning => {
  if (!status) return { color: UNKNOWN_COLOR, tone: 'neutral' };

  const token = canonStatus(status);
  const own = kind ? findIn(STATUS_CATALOGS[MODE_OF_KIND[kind]], token) : undefined;
  const def = own || Object.values(STATUS_CATALOGS).map((defs) => findIn(defs, token)).find(Boolean);
  if (def) return { color: def.color, tone: TONE_OF_STAGE[def.stage] };

  const stage = stageByMeaning(status.toLowerCase());
  return { color: STAGE_COLORS[stage], tone: TONE_OF_STAGE[stage] };
};

export const statusTone = (status: string | null | undefined, kind?: ShipmentKind | null): StatusTone =>
  statusMeaning(status, kind).tone;

export const statusColor = (status: string | null | undefined, kind?: ShipmentKind | null): string =>
  statusMeaning(status, kind).color;

/** Texto accesible del renglón, para lectores de pantalla y para el `title`. */
export const describeNotification = (n: NotificationItem): string => {
  const mode = modeVisual(n.shipmentType);
  const parts = [eventVisual(n.eventType).label];
  if (mode) parts.push(mode.label);
  if (n.status) parts.push(n.status);
  return parts.join(' · ');
};
